import { useEffect, useState } from "react";
import { Play, Youtube, ExternalLink } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function Videos() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/youtube/videos").then((r) => setData(r.data)).catch(() => setData({ configured: false, videos: [] }));
  }, []);

  return (
    <Layout>
      <section className="bg-[#0B0F17] text-white dcep-grain border-b border-white/10">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-14">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-400 mb-3 flex items-center gap-2"><Youtube size={15} /> Video media</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Field recordings & briefings</h1>
          <p className="mt-4 text-white/60 max-w-2xl leading-relaxed">
            The latest videos from the DCEPtNZ channel, updated automatically.
          </p>
          {data?.channel && (
            <a href={data.channel.url} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#0B0F17] text-sm font-medium hover:bg-emerald-400 transition-colors" data-testid="youtube-channel-link">
              Visit the channel <ExternalLink size={14} />
            </a>
          )}
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-12">
        {!data ? (
          <div className="py-20 text-center text-[#52606D]">Loading videos…</div>
        ) : !data.configured ? (
          <div className="py-20 text-center max-w-md mx-auto">
            <Youtube size={40} className="mx-auto text-[#52606D] mb-4" />
            <h2 className="font-serif text-2xl text-[#11181C]">Channel not yet connected</h2>
            <p className="text-[#52606D] mt-3 text-sm leading-relaxed">An administrator can connect the YouTube channel from the Admin Portal. Videos will then appear here automatically.</p>
          </div>
        ) : data.error ? (
          <div className="py-20 text-center text-[#52606D]">{data.error}</div>
        ) : data.videos.length === 0 ? (
          <div className="py-20 text-center text-[#52606D]">No videos found on this channel yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.videos.map((v, i) => (
              <button key={v.videoId} onClick={() => setActive(v)} className="group text-left bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-md transition-shadow" data-testid={`video-card-${i}`}>
                <div className="aspect-video relative overflow-hidden bg-slate-100">
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                    <div className="w-14 h-14 rounded-full bg-[#059669] flex items-center justify-center"><Play size={22} className="text-white ml-1" fill="white" /></div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-sans font-semibold text-[#11181C] leading-snug line-clamp-2">{v.title}</h3>
                  <div className="font-mono text-[11px] text-[#52606D] mt-2">{fmtDate(v.publishedAt)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {active && (
        <div className="fixed inset-0 z-[2000] bg-black/80 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="video-modal">
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video rounded-xl overflow-hidden bg-black">
              <iframe title={active.title} className="w-full h-full" src={`https://www.youtube.com/embed/${active.videoId}?autoplay=1`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
            <div className="flex items-center justify-between mt-4">
              <h3 className="font-serif text-xl text-white">{active.title}</h3>
              <button onClick={() => setActive(null)} className="text-white/70 text-sm hover:text-white" data-testid="video-modal-close">Close ✕</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
